import { onUnmounted, ref } from 'vue'
import { isAllowedChatUploadFile } from '@/modules/chat-matrix-fast/utils/fileUtils'

interface UseChatFileDropOptions {
  /** false — не принимать drop (чат не готов / нет поля ввода) */
  enabled?: () => boolean
  onFiles: (files: File[]) => void
}

/** Drag-and-drop файлов на область чата */
export function useChatFileDrop(options: UseChatFileDropOptions) {
  const isDraggingFiles = ref(false)
  let dragDepth = 0

  const isEnabled = () => options.enabled?.() !== false

  const hasFileDrag = (event: DragEvent) =>
    Boolean(event.dataTransfer?.types?.includes('Files'))

  const filesFromDrop = (dt: DataTransfer | null): File[] => {
    if (!dt?.files?.length) return []
    return Array.from(dt.files).filter(isAllowedChatUploadFile)
  }

  const reset = () => {
    dragDepth = 0
    isDraggingFiles.value = false
  }

  const onDragEnter = (event: DragEvent) => {
    if (!isEnabled() || !hasFileDrag(event)) return
    event.preventDefault()
    dragDepth += 1
    isDraggingFiles.value = true
  }

  const onDragOver = (event: DragEvent) => {
    if (!isEnabled() || !hasFileDrag(event)) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
  }

  const onDragLeave = (event: DragEvent) => {
    if (!hasFileDrag(event)) return
    event.preventDefault()
    dragDepth = Math.max(0, dragDepth - 1)
    if (dragDepth === 0) isDraggingFiles.value = false
  }

  const onDrop = (event: DragEvent) => {
    event.preventDefault()
    reset()
    if (!isEnabled()) return
    const files = filesFromDrop(event.dataTransfer)
    if (files.length) options.onFiles(files)
  }

  onUnmounted(reset)

  return {
    isDraggingFiles,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
  }
}
